import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  width = '100%', 
  height = '1rem',
  rounded = true 
}) => {
  return (
    <div
      className={`skeleton-enhanced ${rounded ? 'rounded' : ''} ${className}`}
      style={{ width, height }}
    />
  );
};

interface SkeletonCardProps {
  className?: string;
  lines?: number;
  showAvatar?: boolean;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ 
  className = '', 
  lines = 3, 
  showAvatar = false 
}) => {
  return (
    <div className={`bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 p-6 ${className}`}>
      <div className="flex items-start space-x-4">
        {showAvatar && (
          <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
        )}
        <div className="flex-1 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          {Array.from({ length: lines }).map((_, i) => (
            <Skeleton key={i} className="h-3" style={{ width: `${Math.max(60, 100 - i * 10)}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
};

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({ 
  rows = 5, 
  columns = 4, 
  className = '' 
}) => {
  return (
    <div className={`bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-slate-50/80 px-6 py-4 border-b border-slate-200">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className="h-4" style={{ width: `${100 / columns}%` }} />
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-slate-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4">
            <div className="flex space-x-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} className="h-4" style={{ width: `${100 / columns}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SkeletonGridProps {
  items?: number;
  columns?: number;
  className?: string;
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({ 
  items = 6, 
  columns = 3, 
  className = '' 
}) => {
  return (
    <div className={`grid gap-6 ${className}`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: items }).map((_, i) => (
        <SkeletonCard key={i} lines={2} showAvatar={true} />
      ))}
    </div>
  );
};

// Specialized skeleton components for specific sections
export const DashboardSkeleton: React.FC = () => (
  <div className="space-y-8 animate-fade-in-up">
    {/* Stats Cards */}
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 p-6">
          <Skeleton className="h-4 w-1/2 mb-2" />
          <Skeleton className="h-8 w-1/3 mb-2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
    
    {/* Calendar Section */}
    <SkeletonCard lines={4} className="h-64" />
    
    {/* Recent Activity */}
    <SkeletonTable rows={3} columns={3} />
  </div>
);

export const ClientsSkeleton: React.FC = () => (
  <div className="space-y-8 animate-fade-in-up">
    {/* Search and Filters */}
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 p-6">
      <Skeleton className="h-4 w-1/4 mb-4" />
      <div className="flex space-x-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
    
    {/* Client Cards */}
    <SkeletonGrid items={9} columns={3} />
  </div>
);

export const BookingsSkeleton: React.FC = () => (
  <div className="space-y-8 animate-fade-in-up">
    {/* Filters */}
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 p-6">
      <div className="flex space-x-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
    
    {/* Bookings Table */}
    <SkeletonTable rows={8} columns={5} />
  </div>
);

export const CertificatesSkeleton: React.FC = () => (
  <div className="space-y-8 animate-fade-in-up">
    {/* Upload Area */}
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/40 p-12 text-center">
      <Skeleton className="h-16 w-16 mx-auto mb-4 rounded-full" />
      <Skeleton className="h-6 w-64 mx-auto mb-2" />
      <Skeleton className="h-4 w-96 mx-auto mb-4" />
      <Skeleton className="h-12 w-48 mx-auto" />
    </div>
  </div>
);
